/**
 * Job Detail — view a single job, apply to it.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Divider,
} from '@design-system';

import { fetchJob } from '../../domains/jobs';
import { applyToJob } from '../../domains/applications';
import type { Job } from '../../domains/jobs';

function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Application form state
  const [coverNote, setCoverNote] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchJob(id)
      .then((data) => {
        setJob(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load job');
        setLoading(false);
      });
  }, [id]);

  const handleApply = async () => {
    if (!id) return;
    setApplying(true);
    setApplyError(null);
    try {
      await applyToJob(id, { coverNote, contactEmail });
      setApplied(true);
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center py-8">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !job) {
    return (
      <Alert severity="error">{error || 'Job not found'}</Alert>
    );
  }

  return (
    <Box>
      <Button onClick={() => navigate('/jobs')} className="mb-4">
        ← Back to Jobs
      </Button>

      <Typography variant="h4" gutterBottom>
        {job.title}
      </Typography>

      <Box className="mb-4 flex flex-wrap gap-2">
        <Chip label={job.category} color="primary" />
        <Chip label={job.type} />
        {job.remote && <Chip label="Remote" color="success" />}
        <Chip
          label={job.status}
          color={job.status === 'open' ? 'success' : 'default'}
          variant="outlined"
        />
      </Box>

      {job.budget && (
        <Typography variant="subtitle1" gutterBottom>
          Budget: {job.budget}
        </Typography>
      )}

      {job.location && (
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Location: {job.location}
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Posted by {job.postedBy.username} • {new Date(job.createdAt).toLocaleDateString()}
      </Typography>

      <Divider className="my-4" />

      <Typography variant="body1" className="mb-4 whitespace-pre-wrap">
        {job.description}
      </Typography>

      <Box className="mb-6 flex flex-wrap gap-1">
        <Typography variant="subtitle2" className="mr-2">Skills:</Typography>
        {job.skills.map((skill) => (
          <Chip key={skill} label={skill} size="small" />
        ))}
      </Box>

      {/* Application Form */}
      {job.status === 'open' && !applied && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Apply to this Job
            </Typography>
            {applyError && (
              <Alert severity="error" className="mb-3">
                {applyError}
              </Alert>
            )}
            <TextField
              label="Cover Note"
              multiline
              rows={4}
              fullWidth
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              className="mb-3"
              placeholder="Tell them why you're a great fit…"
            />
            <TextField
              label="Contact Email"
              type="email"
              fullWidth
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="mb-3"
            />
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applying || coverNote.length < 10 || !contactEmail}
            >
              {applying ? 'Applying…' : 'Submit Application'}
            </Button>
          </CardContent>
        </Card>
      )}

      {applied && (
        <Alert severity="success">
          Your application has been submitted!
        </Alert>
      )}
    </Box>
  );
}

export default JobDetailPage;
